/// <reference types="@angular/localize" />

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { enableProdMode, provideZoneChangeDetection, ErrorHandler, provideAppInitializer, inject, importProvidersFrom } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { ServiceWorkerModule } from '@angular/service-worker'

import { ConfirmationService, MessageService } from 'primeng/api'
import { ButtonModule } from 'primeng/button'
import { CardModule } from 'primeng/card'
import { providePrimeNG } from 'primeng/config'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { DialogModule } from 'primeng/dialog'
import { DividerModule } from 'primeng/divider'
import { DrawerModule } from 'primeng/drawer'
import { FileUploadModule } from 'primeng/fileupload'
import { MenuModule } from 'primeng/menu'
import { RippleModule } from 'primeng/ripple'
import { SelectModule } from 'primeng/select'
import { StyleClassModule } from 'primeng/styleclass'
import { TableModule } from 'primeng/table'
import { TabsModule } from 'primeng/tabs'
import { TagModule } from 'primeng/tag'
import { ToastModule } from 'primeng/toast'
import { environment } from '@environments/environment'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import * as Sentry from '@sentry/angular'

import { isbot } from 'isbot'

import { LongRunningJobService } from '@app/services/long-running-job.service'
import { NetworkService } from '@app/services/network.service'
import { SignalService } from '@app/services/signal.service'
import { UpdateService } from '@app/services/update.service'
import { UserService } from '@app/services/user.service'
import { ConfigureFiltersService } from '@app/components/configure-filters/configure-filters.service'
import { DraftsModule } from '@app/components/drafts/drafts.module'
import { GlobalSearchService } from '@app/components/global-search/global-search.service'
import { LongRunningJobsModule } from '@app/components/long-running-jobs/long-running-jobs.module'
import { SearchModule } from '@app/components/search/search.module'
import { UpdatesModule } from '@app/components/updates/updates.module'
import { ViewerModule } from '@app/components/viewer/viewer.module'
import { AppRoutingModule } from '@app/app-routing.module'
import { AppComponent } from '@app/app.component'
import { isBridgeRequest, runBridge } from '@app/auth/msal-redirect-bridge'
import { provideMsal } from '@app/auth/msal.providers'
import { GlobalErrorInterceptor } from '@app/interceptors/global-error.interceptor'
import { LocaleInterceptor } from '@app/interceptors/locale.interceptor'

const actedPresetTheme = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        primary: {
          50: '#f4f3f7',
          100: '#c8c7da',
          200: '#9d9abc',
          300: '#7A7A7A',
          400: '#464181',
          500: '#1b1464',
          600: '#171155',
          700: '#130e46',
          800: '#0f0b37',
          900: '#0b0828',
          950: '#070519',
        },
      },
      dark: {
        primary: {
          50: '#f6fbf8',
          100: '#d4eddd',
          200: '#b2dec2',
          300: '#8fcfa8',
          400: '#6dc18d',
          500: '#4bb272',
          600: '#409761',
          700: '#357d50',
          800: '#29623f',
          900: '#1e472e',
          950: '#132d1d',
        },
      },
    },
  },
  components: {
    autocomplete: {
      root: {
        disabledBackground: 'transparent',
      },
      css: () => `
        .p-autocomplete .p-autocomplete-dropdown {
          visibility: hidden;
        }
        .p-disabled.p-autocomplete-input-multiple {
          border: 1px solid transparent;
          user-select: text;
          cursor: text !important;
          pointer-events: auto;
        }
        .p-disabled .p-autocomplete-token-icon,
        .p-autocomplete-multiple-container.p-disabled .p-autocomplete-token-icon {
          display: none;
        }
        .p-autocomplete-dropdown:disabled {
          visibility: hidden;
        }
      `,
    },

    button: {
      css: () => `
        .p-badge {
          background: orange;
        }
        .p-button.p-button-black,
        .p-buttonset.p-button-black > .p-button,
        .p-splitbutton.p-button-black > .p-button,
        .p-button.p-button-black:enabled:hover,
        .p-buttonset.p-button-black > .p-button:enabled:hover,
        .p-splitbutton.p-button-black > .p-button:enabled:hover {
          color: #fff;
          background: #000000;
          border: 1px solid #000000;
        }
      `,
    },

    checkbox: {
      root: {
        borderColor: '#ced4da',
      },
      css: () => `
        .p-checkbox.p-disabled .p-checkbox-box {
          background: transparent;
          border-color: var(--p-checkbox-border-color);
          cursor: text;
          pointer-events: auto;
        }
      `,
    },

    card: {
      root: {
        borderRadius: '16px',
      },
      colorScheme: {
        light: {
          root: {
            shadow: '0 -1px 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.07), 0 4px 8px rgba(0,0,0,0.07), 0 8px 16px rgba(0,0,0,0.07)',
            background: 'var(--surface-card)',
          },
        },
        dark: {
          root: {
            shadow: '0 1px 2px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.15), 0 8px 16px rgba(0,0,0,0.15)',
            background: '#18181B',
          },
        },
      },
      css: () => `
        .p-card {
          backdrop-filter: blur(10px);
          margin-bottom: 1.5rem;
        }
        .p-card .p-card-body {
          padding: 1rem 1.75rem;
        }
        .p-card .p-card-content {
          padding: 0;
        }
      `,
    },

    chip: {
      css: () => `
        .p-disabled .p-chip-remove-icon {
          display: none;
        }
      `,
    },

    confirmdialog: {
      css: () => `
        .p-confirmdialog .p-dialog-content {
          align-items: baseline;
          gap: 0;
        }
        .p-confirmdialog .p-confirmdialog-reject-button {
          background-color: #dc3545;
          border-color: #dc3545;
          color: white;
        }
        .p-confirmdialog .p-confirmdialog-reject-button:hover {
          background-color: #c82333;
          border-color: #bd2130;
        }
      `,
    },

    dataview: {
      content: {
        background: 'transparent',
      },
    },

    datatable: {
      root: {
        borderColor: 'transparent',
      },
      headerCell: {
        borderColor: 'transparent',
      },
      bodyCell: {
        padding: '0.45rem',
      },
      colorScheme: {
        light: {
          row: {
            stripedBackground: '{surface.100}',
          },
          headerCell: {
            color: '{surface.50}',
            background: '{surface.600}',
            padding: '0.45rem',
            hoverColor: '{surface.50}',
            hoverBackground: '{surface.600}',
            selectedColor: '{surface.50}',
            selectedBackground: '{surface.600}',
          },
        },
        dark: {
          headerCell: {
            hoverBackground: '{surface.600}',
            background: '{surface.600}',
            selectedBackground: '{surface.600}',
          },
        },
      },
      css: ({ dt }) => `
        .p-datatable-tbody > tr > td {
          font-size: 0.9rem;
          border: none !important;
          border-right: 1px solid var(--p-surface-200) !important;
        }

        .p-datatable-tbody > tr > td:last-child {
          border-right: none !important;
        }

        @media (prefers-color-scheme: dark) {
          .p-datatable-tbody > tr > td {
            border-right-color: var(--p-surface-700) !important;
          }
        }
        .p-datatable-tbody > tr:hover,
        .p-datatable-hoverable .p-datatable-tbody > tr:not(.p-datatable-row-selected):hover {
          background-color: ${dt('surface.200')} !important;
        }
        @media (prefers-color-scheme: dark) {
          .p-datatable-tbody > tr:hover,
          .p-datatable-hoverable .p-datatable-tbody > tr:not(.p-datatable-row-selected):hover {
            background-color: ${dt('surface.800')} !important;
          }
        }
        .p-datatable-row-selected {
          background-color: ${dt('surface.300')} !important;
          border-bottom: transparent !important;
        }
        @media (prefers-color-scheme: dark) {
          .p-datatable-row-selected {
            background-color: ${dt('surface.800')} !important;
          }
        }
        .p-sortable-column-badge {
          margin: 0 0.5rem;
        }
        .p-datatable-column-filter-button {
          margin-left: 0.25rem;
        }
        .p-sortable-column-icon {
          margin-left: 0.5rem;
        }
        .active-filter .p-button {
          background-color: orange !important;
          color: var(--p-badge-primary-color);
        }
        .p-datatable-resizable-table > .p-datatable-tfoot > tr > td,
        .p-datatable-resizable-table > .p-datatable-tbody > tr > td {
          white-space: normal !important;
        }
        .p-datatable-loading-overlay .p-datatable-loading-icon {
          zoom: 5;
        }
        .p-selectable-row {
          cursor: pointer;
        }
        .p-selectable-row:hover:not(.p-highlight) {
          background: #f5f5f5 !important;
        }
      `,
    },

    datepicker: {
      css: () => `
        .p-datepicker,
        .p-datepicker .p-datepicker-header,
        .p-datepicker .p-datepicker-buttonbar,
        .p-datepicker table th,
        .p-datepicker table td {
          padding: 0;
        }
        .p-datepicker.ng-invalid .p-datepicker.p-inputwrapper .p-inputtext {
          box-shadow: inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px var(--p-inputtext-invalid-border-color);
          border-color: var(--p-inputtext-invalid-border-color);
        }
        button.p-datepicker-dropdown:disabled {
          display: none;
        }
        .p-disabled .p-datepicker-dropdown {
          visibility: hidden;
        }
      `,
    },

    dialog: {
      css: () => `
        .p-dialog-content div.grid.fluid {
          margin: 5px 0 10px 0;
        }
      `,
    },

    drawer: {
      content: {
        padding: '0',
      },
    },

    fileupload: {
      progressbar: {
        height: '0',
      },
      css: () => `
        .p-fileupload .p-fileupload-buttonbar .p-button.p-fileupload-choose .p-button-label {
          margin-left: 0.5em;
        }
      `,
    },

    inputtext: {
      root: {
        disabledColor: 'var(--p-inputtext-color)',
      },
      css: () => `
        .p-inputtext[disabled] {
          box-shadow: none;
        }
        .p-inputtext:read-only:not(.p-autocomplete-multiple-container) {
          background: transparent;
          border: 1px solid transparent;
          box-shadow: none;
        }
        .p-inputtext:read-only:hover {
          border: 1px solid transparent;
        }
        .p-component::-webkit-input-placeholder {
          opacity: 0.5;
        }
        .p-component:read-only::-webkit-input-placeholder,
        .p-component:read-only::-moz-placeholder {
          color: transparent;
        }
      `,
    },

    menu: {
      css: () => `
        li.p-menu-item.p-hidden {
          display: none;
        }
      `,
    },

    multiselect: {
      root: {
        disabledBackground: 'transparent',
      },
      css: () => `
        .p-multiselect-label {
          display: flex;
          flex-wrap: wrap !important;
        }
        .p-multiselect.p-disabled {
          background: transparent;
          border: 1px solid transparent;
          user-select: text;
          cursor: text !important;
          pointer-events: auto;
          box-shadow: none;
        }
        .p-multiselect.p-disabled > .p-multiselect-dropdown {
          display: none;
        }
        .p-multiselect.p-multiselect-chip .p-multiselect-token {
          padding: 0.25rem 0.5rem 0.35rem 0.5rem;
          border-radius: 4px;
          margin-bottom: 2px;
        }
      `,
    },

    select: {
      root: {
        disabledColor: '{form.field.color}',
      },
      css: () => `
        .p-select.p-disabled span,
        .p-disabled .p-chip-label {
          background: transparent;
          user-select: text;
          cursor: text !important;
          pointer-events: auto;
        }
        .p-select.p-disabled {
          background: transparent;
          border: 1px solid transparent;
          box-shadow: none;
        }
        .p-select.p-disabled > .p-select-dropdown {
          display: none;
        }
        .p-disabled span.p-select-label {
          overflow: auto;
          text-wrap: pretty;
        }
      `,
    },

    splitbutton: {
      css: () => `
        .p-tieredmenu {
          font-size: 1rem;
        }
      `,
    },

    tabs: {
      colorScheme: {
        light: {
          tab: {
            activeBackground: '{primary.500}',
            activeColor: '{primary.50}',
          },
        },
        dark: {
          tab: {
            activeBackground: '{surface.600}',
            activeColor: '{surface.300}',
          },
        },
      },
      css: () => `
        .p-tab-active {
          border-radius: var(--p-border-radius-md) var(--p-border-radius-md) 0 0;
        }
      `,
    },

    tag: {
      css: () => `
        .p-tag {
          margin: 1px 0;
        }
      `,
    },

    textarea: {
      css: () => `
        .p-textarea[readonly],
        .p-textarea:disabled:not(.p-autocomplete-multiple-container) {
          border: 1px solid transparent;
          box-shadow: none;
          background: transparent;
        }
        .p-textarea[readonly]:focus {
          border: 1px solid transparent;
        }
        .p-textarea:read-only:hover {
          border: 1px solid transparent;
        }
      `,
    },

    togglebutton: {
      root: {
        background: '{surface.100}',
        borderColor: '{surface.100}',
        checkedBackground: '{primary.100}',
        checkedBorderColor: '{surface.100}',
      },
    },

    toolbar: {
      colorScheme: {
        light: {
          root: {
            color: '#000',
          },
        },
        dark: {
          root: {
            color: '#FFFFFF',
          },
        },
      },
      css: () => `
        .detail-toolbar {
          margin-bottom: 1rem;
          padding: 0.5rem;
          min-height: 50px;
          font-size: 1.5rem;
          font-weight: 500;
          border: none;
          color: var(--p-toolbar-color);
        }
        .list-toolbar.p-toolbar {
          padding: 0.5rem;
          font-size: 1.5rem;
          font-weight: 600;
          border: none;
          background: none;
        }
        .p-toolbar-group-left,
        .p-toolbar-group-center,
        .p-toolbar-group-right {
          align-items: center;
          display: flex;
        }
        .p-toolbar-group-center,
        .p-toolbar-group-right {
          flex-wrap: wrap;
        }
        @media screen and (max-width: 640px) {
          .p-toolbar p-button button:has(i.fa) .p-button-label,
          .p-toolbar p-button button:has(.p-button-icon-left) .p-button-label {
            display: none;
          }
        }
      `,
    },
  },
})

if (environment.production) {
  enableProdMode()
}

Sentry.init({
  enabled: !!window.env?.SENTRY_DSN && !isbot(navigator.userAgent),
  dsn: window.env?.SENTRY_DSN,
  environment: window.env?.SENTRY_ENVIRONMENT,
  release: window.env?.ACTED_ARCHIVES_COMMIT_SHA,
  // Leaving dataCollection unset collects user info (IP address), cookies, full headers and bodies since v11:
  // keep the restrictive v10 baseline. The user is still sent explicitly by GlobalErrorInterceptor.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: {
      request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
    },
    httpBodies: [],
    urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    graphQL: { document: false, variables: false },
  },

  // A synthetic stack trace on captureMessage would regroup the MSAL telemetry issue (actedparis/acted-core#252)
  // and mark each session as errored
  attachStacktrace: false,

  // Adds sentry-trace and baggage headers to backend requests, so a frontend error and the backend error it
  // triggered share a trace id and show up linked in Sentry
  integrations: [Sentry.browserTracingIntegration()],

  // Trace propagation only: no span is sent, so no performance quota is used
  tracesSampleRate: 0,

  tracePropagationTargets: [window.env?.API_BACKEND_URL],
})

// The authorization server is handing back a response on the bridge path, so hand it to the main frame
// instead of starting the app. Skipping the bootstrap is the point: it keeps the service worker, the MSAL
// client, the permissions request and AppComponent's own handleRedirectObservable out of the hidden iframe,
// and out of the 10s the library allows the bridge to answer in.
if (isBridgeRequest(window.location)) void runBridge()
else bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      FormsModule,
      DraftsModule,
      ButtonModule,
      FileUploadModule,
      ConfirmDialogModule,
      DialogModule,
      TableModule,
      TagModule,
      RippleModule,
      SelectModule,
      CardModule,
      StyleClassModule,
      DividerModule,
      MenuModule,
      DrawerModule,
      TabsModule,
      ToastModule,
      ViewerModule,
      SearchModule,
      SelectModule,
      LongRunningJobsModule,
      UpdatesModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.serviceWorkerEnabled,
        // Register the ServiceWorker as soon as the app is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      })),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // must stay ahead of the other HTTP_INTERCEPTORS providers: MsalInterceptor runs first
    ...provideMsal(),
    {
      provide: HTTP_INTERCEPTORS, // Provides as HTTP Interceptor
      useClass: LocaleInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorInterceptor,
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService)
    }),
    ConfirmationService,
    SignalService,
    UserService,
    LongRunningJobService,
    GlobalSearchService,
    UpdateService,
    MessageService,
    NetworkService,
    ConfigureFiltersService,
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: actedPresetTheme,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'app-styles, primeng',
          },
        },
      },
    }),
  ],
}).catch(err => console.error(err))
